import React, { useState } from 'react';
import { Button, Checkbox, Col, Form, InputNumber, Typography } from 'antd';
import {
  OrganizationSetting,
  UpdateOrganizationSettingMutation,
} from 'works/graphql/organization-setting';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';

const { Title } = Typography;

type FormValues = {
  useDisputeClosing: boolean | undefined;
  disputesDaysToClose: number | undefined;
};

export const InvoiceDisputesForm = ({
  organizationSetting,
}: {
  organizationSetting: OrganizationSetting | undefined;
}) => {
  const { useDisputeClosing, disputesDaysToClose } = organizationSetting || {};
  const [form] = Form.useForm<FormValues>();
  const [touched, setTouched] = useState(false);
  const [requireInput, setRequireInput] = useState(useDisputeClosing);

  const [updateOrgSetting, { loading: updateOrgSettingLoading }] = useMutation(
    UpdateOrganizationSettingMutation,
    {
      onCompleted: () => {
        setTouched(false);
        createMessage('success', 'Billing settings saved.');
      },
      onError: () =>
        createMessage(
          'error',
          'Failed to save billing settings. Please try again.'
        ),
    }
  );

  const onSubmit = () => {
    updateOrgSetting({
      variables: {
        input: form.getFieldsValue(),
      },
    });
  };

  return (
    <>
      <div className="admin-organization-invoice-disputes-section">
        <Title level={5}>Invoice Disputes</Title>
      </div>
      <Form
        className="admin-organization-invoice-dispute-form"
        onFinish={onSubmit}
        layout="vertical"
        form={form}
        initialValues={{
          useDisputeClosing: useDisputeClosing,
          disputesDaysToClose: disputesDaysToClose,
        }}
        onValuesChange={(changes) => {
          setTouched(true);
          setRequireInput(form.getFieldsValue().useDisputeClosing);
          form.setFieldsValue({ ...form.getFieldsValue(), ...changes });
        }}
      >
        <Col span={6}>
          <Form.Item name="useDisputeClosing" valuePropName="checked">
            <Checkbox className="dispute-resolution-checkbox">
              Enable dispute resolution period
            </Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="How long after an invoice is created can suppliers open a dispute?"
            name="disputesDaysToClose"
            rules={[
              {
                required: requireInput,
                message: 'Please enter an number of days',
              },
            ]}
          >
            <InputNumber
              className="dispute-closing-days-input"
              size="large"
              min={0}
              addonAfter="Days"
              disabled={!requireInput}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item>
            <Button
              className="submit-invoice-dispute-form"
              type="primary"
              size="large"
              disabled={!organizationSetting || !touched}
              onClick={form.submit}
              loading={updateOrgSettingLoading}
            >
              Save
            </Button>
          </Form.Item>
        </Col>
      </Form>
      <style jsx>{`
        .admin-organization-invoice-disputes-section {
          margin-top: 24px;
        }
        :global(.admin-organization-invoice-dispute-form .ant-form-item-label) {
          margin-bottom: 4px;
        }
      `}</style>
    </>
  );
};
